import * as React from 'react';
import { useRecoilState } from 'recoil';

import type { NavigationScreenState } from '@gitbook/app-navigation';
import {
    CollapsibleSidebar,
    LoadingPanel,
    ResizableSidebar,
    SIDEBAR_INITIAL_WIDTH,
    useScreenType,
} from '@gitbook/spine-react';

import { shortcuts, useKeyboardShortcuts } from '../Keyboard';
import {
    desktopSidebarCollapsedAtom,
    desktopSidebarWidthAtom,
    useIsSidebarLocked,
    useUnlockSidebar,
} from '../state';
import { AppBanner } from './AppBanner';
import { MobileHeader } from './MobileHeader';

interface LayoutSidebarContextValue {
    isCollapsed: boolean;
    desktopSidebarWidth: number;
}

/**
 * Global context of the rendering mode of the application.
 */
export const LayoutSidebarContext = React.createContext<LayoutSidebarContextValue>({
    isCollapsed: false,
    desktopSidebarWidth: 0,
});

/**
 * Main application layout with the sidebar.
 */
export function AppLayout(props: {
    screen: NavigationScreenState;
    sidebar: React.ReactNode;
    children: React.ReactNode;
}): React.ReactElement {
    const { sidebar, screen, children } = props;
    const [isMenuOpen, setMenuOpen] = React.useState(false);
    const [isCollapsed, setCollapse] = useRecoilState(desktopSidebarCollapsedAtom);
    const [width, setWidth] = useRecoilState(desktopSidebarWidthAtom);
    const { isMobile } = useScreenType();
    const sidebarContext = React.useMemo(
        () => ({
            isCollapsed,
            desktopSidebarWidth: isCollapsed ? 0 : (width ?? SIDEBAR_INITIAL_WIDTH),
        }),
        [isCollapsed, width]
    );

    return (
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-side">
            <AppBanner />
            <div className="flex min-h-0 min-w-0 flex-1">
                {isMobile ? (
                    <MobileSidebar isOpen={isMenuOpen} onOpenChange={setMenuOpen}>
                        {sidebar}
                    </MobileSidebar>
                ) : (
                    <DesktopSidebar
                        isCollapsed={isCollapsed}
                        onCollapseChange={setCollapse}
                        width={width}
                        onWidthChange={setWidth}
                    >
                        {sidebar}
                    </DesktopSidebar>
                )}
                <LayoutSidebarContext.Provider value={sidebarContext}>
                    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-side">
                        <React.Suspense
                            fallback={
                                <LoadingPanel
                                    size="xlarge"
                                    className="m-1.5 rounded-lg border-0.5 border-control"
                                />
                            }
                        >
                            {isMobile && (
                                <MobileHeader
                                    onMenuOpenChange={setMenuOpen}
                                    isMenuOpen={isMenuOpen}
                                    screen={screen}
                                />
                            )}
                            <div
                                // The app framing styles can't be applied in a single location with our current DOM architecture. Screens with sidepanels require applying the framing styles on a more local level instead of in AppLayout.
                                className="[&:not(:has([data-side-panel-frame]))]:app-framing-styles flex min-h-0 min-w-0 flex-1 flex-col"
                            >
                                {children}
                            </div>
                        </React.Suspense>
                    </div>
                </LayoutSidebarContext.Provider>
            </div>
        </div>
    );
}

function MobileSidebar(props: {
    children: React.ReactNode;
    isOpen: boolean;
    onOpenChange: (opened: boolean) => void;
}) {
    const { children, isOpen, onOpenChange } = props;
    return (
        <CollapsibleSidebar isOpen={isOpen} onOpenChange={onOpenChange}>
            {children}
        </CollapsibleSidebar>
    );
}

function DesktopSidebar(props: {
    children: React.ReactNode;
    isCollapsed: boolean;
    onCollapseChange: (collapsed: boolean) => void;
    width: number | null;
    onWidthChange: (width: number | null) => void;
}) {
    const { children, isCollapsed, onCollapseChange, width, onWidthChange } = props;
    const locked = useIsSidebarLocked();
    const unlock = useUnlockSidebar();
    const { isTablet } = useScreenType();
    useKeyboardShortcuts(
        {
            [shortcuts.TOGGLE_SIDEBAR]: () => {
                onCollapseChange(!isCollapsed);
            },
        },
        ['application', 'editor-selection-collapsed']
    );
    return (
        <ResizableSidebar
            isCollapsed={locked || isCollapsed}
            onCollapseChange={(collapsed) => {
                if (!collapsed) {
                    unlock();
                }
                onCollapseChange(collapsed);
            }}
            width={width}
            onWidthChange={onWidthChange}
            expandMode={isTablet ? 'manual' : 'auto'}
        >
            {children}
        </ResizableSidebar>
    );
}
